import { useEffect, useMemo, useState } from 'react';

import { countrySetKey, useRegistryProductsFor } from '@/hooks/useRegistryCatalogue';
import type { RegistryProduct } from '@/types/registry-documents';
import { sharedProducts } from './orderDraft';

/** The catalogues keyed in checklist order, so the shared list reads like
 * the first company's catalogue and the omitted note names countries in
 * the order the analyst sees them. */
function inChecklistOrder(
  countries: string[],
  catalogues: Record<string, RegistryProduct[]>
): Record<string, RegistryProduct[]> {
  const ordered: Record<string, RegistryProduct[]> = {};
  for (const country of countries) {
    if (catalogues[country]) {
      ordered[country] = catalogues[country];
    }
  }
  return ordered;
}

/**
 * The documents step when the order is for every company: the catalogues
 * of every country involved, the documents all of them offer, what is
 * ticked, and the note that goes with every order. A change in the set of
 * countries drops the ticks - the intersection is a different list.
 */
export function useOrderSharedDocuments(opened: boolean, countries: string[], active: boolean) {
  const [selected, setSelected] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const catalogue = useRegistryProductsFor(countries, opened && active);
  const key = countrySetKey(countries);

  useEffect(() => {
    if (!opened) {
      setSelected([]);
      setNotes('');
    }
  }, [opened]);

  useEffect(() => {
    setSelected([]);
  }, [key]);

  const { offered, omitted } = useMemo(
    () => sharedProducts(inChecklistOrder(countries, catalogue.catalogues)),
    [countries, catalogue.catalogues]
  );

  const chosen = useMemo(
    () => offered.filter((product) => selected.includes(product.code)),
    [offered, selected]
  );

  return {
    catalogue,
    /** The documents every country offers. */
    offered,
    /** The codes left out, and the countries that lack them. */
    omitted,
    selected,
    setSelected,
    notes,
    setNotes,
    chosen,
    /** Chosen documents that at least one country quotes. */
    quoteCount: chosen.filter((product) => product.anyQuote).length,
  };
}

export type OrderSharedDocuments = ReturnType<typeof useOrderSharedDocuments>;
